import { BadgeToClipboard } from "@/components/badge-to-clipboard/badge-to-clipboard";
import { CopyToClipboard } from "@/components/copy-to-clipboard/copy-to-clipboard";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { Button, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronDown, ChevronUp, Search } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface SystemInfoProps {
  label: string;
  values?: (string | null)[];
  icon?: React.ReactNode;
}

export function SystemInfo({ label, values, icon }: SystemInfoProps) {
  const { t } = useTranslation(["common", "masterdata"]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const cleanValues = values?.filter((value): value is string => value != null && value.trim() !== "") || [];
  const hasValues = cleanValues.length > 0;
  const filteredValues = hasValues
    ? cleanValues.filter((value) => value.toLowerCase().includes(searchTerm.toLowerCase()))
    : [];

  const shouldShowExpandButton = hasValues && filteredValues.length > 3;
  const shouldShowSearch = hasValues && values!.length > 2;
  const displayValues = shouldShowExpandButton && !isExpanded ? filteredValues.slice(0, 3) : filteredValues;
  const allValues = filteredValues.join(", ");

  return (
    <div className="bg-card flex h-full flex-col rounded-lg border p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-card-foreground font-semibold">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          {hasValues && <CopyToClipboard value={allValues} tooltip={t("masterdata:CopyAllValues")} />}
          {shouldShowExpandButton && (
            <Button
              variant={ButtonVariant.Ghost}
              size={ButtonSize.Sm}
              onClick={() => setIsExpanded(!isExpanded)}
              className="h-6 w-6 p-0"
              aria-label={isExpanded ? t("common:Collapse") : t("common:Expand")}
            >
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          )}
        </div>
      </div>

      {shouldShowSearch && (
        <div className="mb-3">
          <div className="relative">
            <Search className="text-muted-foreground absolute top-1/2 left-2 h-3 w-3 -translate-y-1/2 transform" />
            <Input
              placeholder={t("common:Search")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-7 pl-7 text-xs"
            />
          </div>
        </div>
      )}

      <div className="flex-1">
        {hasValues ? (
          <div className="max-h-32 overflow-y-auto pr-2">
            <div className="flex flex-wrap gap-1.5">
              {displayValues.map((value) => (
                <BadgeToClipboard value={value} key={`badge_${value}`} />
              ))}
              {shouldShowExpandButton && !isExpanded && filteredValues.length > 3 && (
                <Badge
                  variant={BadgeVariant.Outlined}
                  colorVariant={BadgeColorVariant.Secondary}
                  onClick={() => setIsExpanded(true)}
                >
                  +{filteredValues.length - 3} {t("common:More")}
                </Badge>
              )}
            </div>
          </div>
        ) : (
          <span className="text-muted-foreground text-sm italic">{t("masterdata:NoData")}</span>
        )}
      </div>
    </div>
  );
}
